import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { ChatMessage } from '../types';

export interface ChatMessageData extends ChatMessage {
  createdAt: Timestamp;
  updatedAt: Timestamp;
  isEdited?: boolean;
  isDeleted?: boolean;
}

class ChatService {
  private activeChatListeners: Map<string, () => void> = new Map(); // Unsubscribe functions

  // Send a message
  async sendMessage(
    sessionId: string,
    userId: string,
    userName: string,
    userColor: string,
    userAvatar: string,
    content: string,
    messageType: 'text' | 'calculation' | 'variable' | 'system' = 'text'
  ): Promise<string> {
    try {
      const messageData: Omit<ChatMessageData, 'id'> = {
        userId,
        userName,
        userColor,
        userAvatar,
        content,
        messageType,
        timestamp: new Date(),
        createdAt: serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp,
        isEdited: false,
        isDeleted: false
      };

      const docRef = await addDoc(collection(db, 'sessions', sessionId, 'messages'), messageData);
      
      // Update session activity
      const sessionRef = doc(db, 'sessions', sessionId);
      await updateDoc(sessionRef, {
        lastActivityAt: serverTimestamp()
      });
      
      return docRef.id;
    } catch (error) {
      throw new Error(`Failed to send message: ${error}`);
    }
  }

  // Edit a message
  async editMessage(sessionId: string, messageId: string, newContent: string): Promise<void> {
    try {
      const messageRef = doc(db, 'sessions', sessionId, 'messages', messageId);
      await updateDoc(messageRef, {
        content: newContent,
        updatedAt: serverTimestamp(),
        isEdited: true
      });
    } catch (error) {
      throw new Error(`Failed to edit message: ${error}`);
    }
  }

  // Delete a message
  async deleteMessage(sessionId: string, messageId: string): Promise<void> {
    try {
      const messageRef = doc(db, 'sessions', sessionId, 'messages', messageId);
      await updateDoc(messageRef, {
        content: '[Message deleted]',
        updatedAt: serverTimestamp(),
        isDeleted: true
      });
    } catch (error) {
      throw new Error(`Failed to delete message: ${error}`);
    }
  }

  // Get recent messages
  async getRecentMessages(sessionId: string, limitCount: number = 50): Promise<ChatMessageData[]> {
    try {
      const q = query(
        collection(db, 'sessions', sessionId, 'messages'),
        where('isDeleted', '==', false),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        ...doc.data() as ChatMessageData,
        id: doc.id
      })).reverse(); // Reverse to get chronological order
    } catch (error) {
      throw new Error(`Failed to get recent messages: ${error}`);
    }
  }

  // Get messages by type
  async getMessagesByType(
    sessionId: string,
    messageType: 'text' | 'calculation' | 'variable' | 'system',
    limitCount: number = 20
  ): Promise<ChatMessageData[]> {
    try {
      const q = query(
        collection(db, 'sessions', sessionId, 'messages'),
        where('messageType', '==', messageType),
        where('isDeleted', '==', false),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        ...doc.data() as ChatMessageData,
        id: doc.id
      })).reverse();
    } catch (error) {
      throw new Error(`Failed to get messages by type: ${error}`);
    }
  }

  // Search messages
  async searchMessages(
    sessionId: string,
    searchTerm: string,
    limitCount: number = 20
  ): Promise<ChatMessageData[]> {
    try {
      // Note: Firestore doesn't support full-text search natively
      // This is a simple implementation that searches message content
      const q = query(
        collection(db, 'sessions', sessionId, 'messages'),
        where('isDeleted', '==', false),
        orderBy('createdAt', 'desc'),
        limit(limitCount * 2) // Get more to filter client-side
      );
      
      const querySnapshot = await getDocs(q);
      const messages = querySnapshot.docs.map(doc => ({
        ...doc.data() as ChatMessageData,
        id: doc.id
      }));
      
      // Filter messages that contain the search term
      const filteredMessages = messages.filter(message =>
        message.content.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      return filteredMessages.slice(0, limitCount);
    } catch (error) {
      throw new Error(`Failed to search messages: ${error}`);
    }
  }

  // Listen to new messages
  onNewMessages(
    sessionId: string,
    callback: (messages: ChatMessageData[]) => void,
    limitCount: number = 50
  ): () => void {
    const q = query(
      collection(db, 'sessions', sessionId, 'messages'),
      where('isDeleted', '==', false),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const messages = querySnapshot.docs.map(doc => ({
        ...doc.data() as ChatMessageData,
        id: doc.id
      })).reverse(); // Reverse to get chronological order
      
      callback(messages);
    });
    
    // Store unsubscribe function
    this.activeChatListeners.set(sessionId, unsubscribe);
    
    return unsubscribe;
  }

  // Listen to messages by type
  onMessagesByType(
    sessionId: string,
    messageType: 'text' | 'calculation' | 'variable' | 'system',
    callback: (messages: ChatMessageData[]) => void,
    limitCount: number = 20
  ): () => void {
    const q = query(
      collection(db, 'sessions', sessionId, 'messages'),
      where('messageType', '==', messageType),
      where('isDeleted', '==', false),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const messages = querySnapshot.docs.map(doc => ({
        ...doc.data() as ChatMessageData,
        id: doc.id
      })).reverse();
      
      callback(messages);
    });
    
    return unsubscribe;
  }

  // Send system message
  async sendSystemMessage(
    sessionId: string,
    content: string
  ): Promise<string> {
    return this.sendMessage(
      sessionId,
      'system',
      'System',
      '#64748b',
      '🤖',
      content,
      'system'
    );
  }

  // Send calculation message
  async sendCalculationMessage(
    sessionId: string,
    userId: string,
    userName: string,
    userColor: string,
    userAvatar: string,
    expression: string,
    result: string
  ): Promise<string> {
    const content = `${expression} = ${result}`;
    return this.sendMessage(
      sessionId,
      userId,
      userName,
      userColor,
      userAvatar,
      content,
      'calculation'
    );
  }

  // Send variable message
  async sendVariableMessage(
    sessionId: string,
    userId: string,
    userName: string,
    userColor: string,
    userAvatar: string,
    variableName: string,
    variableValue: string
  ): Promise<string> {
    const content = `Defined variable: ${variableName} = ${variableValue}`;
    return this.sendMessage(
      sessionId,
      userId,
      userName,
      userColor,
      userAvatar,
      content,
      'variable'
    );
  }

  // Clean up old messages (called by cloud function)
  async cleanupOldMessages(sessionId: string, maxMessages: number = 200): Promise<void> {
    try {
      const q = query(
        collection(db, 'sessions', sessionId, 'messages'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.docs.length > maxMessages) {
        const messagesToDelete = querySnapshot.docs.slice(maxMessages);
        
        for (const doc of messagesToDelete) {
          await deleteDoc(doc.ref);
        }
      }
    } catch (error) {
      console.error('Failed to cleanup old messages:', error);
    }
  }

  // Listen for chat messages
  listenForChatMessages(
    sessionId: string,
    callback: (messages: ChatMessageData[]) => void
  ): () => void {
    const messagesRef = collection(db, 'sessions', sessionId, 'messages');
    const q = query(
      messagesRef,
      where('isDeleted', '==', false),
      orderBy('createdAt', 'asc')
    );
    
    const unsubscribe = onSnapshot(q, 
      (querySnapshot) => {
        const messages: ChatMessageData[] = [];
        querySnapshot.forEach((doc) => {
          messages.push({
            ...doc.data() as ChatMessageData,
            id: doc.id
          });
        });
        callback(messages);
      },
      (error) => {
        console.error('Chat listener error:', error);
        // Attempt to reconnect after a delay
        setTimeout(() => {
          console.log('Attempting to reconnect chat listener...');
          this.listenForChatMessages(sessionId, callback);
        }, 5000);
      }
    );
    
    return unsubscribe;
  }

  // Clear all messages for a session
  async clearMessages(sessionId: string): Promise<void> {
    try {
      const messagesRef = collection(db, 'sessions', sessionId, 'messages');
      const querySnapshot = await getDocs(messagesRef);
      
      const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
    } catch (error) {
      throw new Error(`Failed to clear messages: ${error}`);
    }
  }

  // Clean up all listeners
  cleanup(): void {
    this.activeChatListeners.forEach(unsubscribe => unsubscribe());
    this.activeChatListeners.clear();
  }
}

// Export singleton instance
export const chatService = new ChatService();
export default chatService;
